import React, { useState, useContext, useEffect } from 'react';
import { 
  ArrowLeft, 
  PhoneCall,
  AlertCircle, 
  RefreshCw, 
  Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppContext from '../../context/AppContext';
import { database, ref, set } from '../../FirebaseConfig';

const ConfigurationRetrait = () => {
  const navigate = useNavigate();
  const { userInfos, id } = useContext(AppContext);
  
  const [numeroTransfert, setNumeroTransfert] = useState('');
  const [modePaiement, setModePaiement] = useState('orange-money-ci');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Initialiser les valeurs avec les données utilisateur lorsqu'elles sont chargées
  useEffect(() => {
    if (userInfos && userInfos.retraitAuto) {
      setNumeroTransfert(userInfos.retraitAuto.numeroTransfert || '');
      // Convertir l'ancien format vers le nouveau si nécessaire
      const oldModePaiement = userInfos.retraitAuto.modePaiement || '';
      if (oldModePaiement.includes('_')) {
        // Convertir de l'ancien format (ex: orange_ci) au nouveau format (ex: orange-money-ci)
        const [operator, country] = oldModePaiement.split('_');
        if (operator === 'orange' && country === 'ci') {
          setModePaiement('orange-money-ci');
        } else if (operator === 'mtn' && country === 'ci') {
          setModePaiement('mtn-ci');
        } else if (operator === 'wave' && country === 'ci') {
          setModePaiement('wave-ci');
        } else if (operator === 'moov' && country === 'benin') {
          setModePaiement('moov-benin');
        } else if (operator === 'mtn' && country === 'benin') {
          setModePaiement('mtn-benin');
        } else {
          setModePaiement('orange-money-ci'); // Valeur par défaut
        }
      } else {
        // Utiliser directement la valeur si elle est déjà au nouveau format
        setModePaiement(userInfos.retraitAuto.modePaiement || 'orange-money-ci');
      }
    }
  }, [userInfos]);
  
  const handleSubmit = async () => {
    // Validation
    if (!numeroTransfert || numeroTransfert.length < 6) {
      setError('Veuillez saisir un numéro de transfert valide (minimum 6 chiffres)');
      return;
    }
    
    // Traitement
    setIsBusy(true);
    setError('');
    
    try {
      // Convertir le mode de paiement au format approprié pour l'API et l'affichage
      let operateur, pays;
      
      // Extraire les informations en fonction du nouveau format
      if (modePaiement === 'orange-money-ci' || modePaiement === 'orange-money-senegal' || 
          modePaiement === 'orange-money-mali' || modePaiement === 'orange-money-burkina') {
        operateur = 'ORANGE';
        if (modePaiement === 'orange-money-ci') pays = 'CI';
        else if (modePaiement === 'orange-money-senegal') pays = 'SENEGAL';
        else if (modePaiement === 'orange-money-mali') pays = 'MALI';
        else if (modePaiement === 'orange-money-burkina') pays = 'BURKINA';
      } else if (modePaiement === 'mtn-ci' || modePaiement === 'mtn-benin') {
        operateur = 'MTN';
        pays = modePaiement === 'mtn-ci' ? 'CI' : 'BENIN';
      } else if (modePaiement === 'moov-ci' || modePaiement === 'moov-benin' || 
                 modePaiement === 'moov-togo' || modePaiement === 'moov-burkina-faso') {
        operateur = 'MOOV';
        if (modePaiement === 'moov-ci') pays = 'CI';
        else if (modePaiement === 'moov-benin') pays = 'BENIN';
        else if (modePaiement === 'moov-togo') pays = 'TOGO';
        else if (modePaiement === 'moov-burkina-faso') pays = 'BURKINA';
      } else if (modePaiement === 'wave-ci' || modePaiement === 'wave-senegal') {
        operateur = 'WAVE';
        pays = modePaiement === 'wave-ci' ? 'CI' : 'SENEGAL';
      } else if (modePaiement === 'free-money-senegal') {
        operateur = 'FREE';
        pays = 'SENEGAL';
      } else if (modePaiement === 'expresso-senegal') {
        operateur = 'EXPRESSO';
        pays = 'SENEGAL';
      } else if (modePaiement === 't-money-togo') {
        operateur = 'TMONEY';
        pays = 'TOGO';
      } else {
        // Valeurs par défaut si le format ne correspond à aucun cas connu
        operateur = 'ORANGE';
        pays = 'CI';
      }
      
      // Créer l'objet de configuration du retrait automatique
      const retraitAutoConfig = {
        active: true,
        numeroTransfert: numeroTransfert,
        modePaiement: modePaiement,
        operateur: operateur,
        pays: pays,
        updatedAt: new Date().toISOString()
      };
      
      // Mettre à jour dans Firebase
      const retraitAutoRef = ref(database, `vendeur/${id}/retraitAuto`);
      await set(retraitAutoRef, retraitAutoConfig);
      
      console.log("Configuration de paiement automatique enregistrée:", retraitAutoConfig);
      
      // Afficher message de succès sans redirection automatique
      setIsSuccess(true);
    } catch (error) {
      console.error("Erreur lors de la configuration du paiement automatique:", error);
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsBusy(false);
    }
  };
  
  // Bottom Sheet de succès
  if (isSuccess) {
    return (
      <div className="fixed inset-0 z-50">
        {/* Overlay avec animation de fade in */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
        
        {/* Bottom Sheet de succès */}
        <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl p-6">
          <div className="text-center py-8">
            <div className="bg-green-100 text-green-600 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <Check className="w-10 h-10" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Configuration réussie!</h1>
            <p className="text-gray-600 mb-6">
              Vos paiements seront désormais envoyés automatiquement sur votre compte Mobile Money.
            </p>
            
            <div className="flex gap-3">
              <button 
                onClick={() => navigate(-1)}
                className="flex-1 py-4 bg-indigo-600 text-white rounded-xl font-medium shadow-sm"
              >
                Revenir au retrait
              </button>
              
              <button 
                onClick={() => navigate('/IndexEspace')}
                className="flex-1 py-4 bg-gray-100 text-gray-700 rounded-xl font-medium"
              >
                Aller à l'accueil
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* En-tête */}
      <div className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center py-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-100 mr-3"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Paiements automatiques</h1>
          </div>
        </div>
      </div>
      
      {/* Contenu principal */}
      <div className="max-w-lg mx-auto px-4 py-6">
        <div className="bg-indigo-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-indigo-700 leading-relaxed">
            Recevez vos revenus instantanément et automatiquement sur votre compte Mobile Money dès qu'un paiement est effectué. Plus besoin de faire des demandes manuelles !
          </p>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Configuration</h2>
            
            {/* Numéro pour le transfert */}
            <div className="mb-5">
              <label htmlFor="numeroTransfert" className="block text-sm font-medium text-gray-700 mb-2">
                Numéro Mobile Money (minimum 6 chiffres) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <PhoneCall className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  inputMode="numeric"
                  id="numeroTransfert"
                  value={numeroTransfert}
                  onChange={(e) => setNumeroTransfert(e.target.value)}
                  placeholder="Ex: 071234"
                  className="block w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">Ce numéro recevra automatiquement tous vos paiements</p>
            </div>
            
            {/* Mode de paiement */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Service de paiement <span className="text-red-500">*</span>
              </label>
              
              {/* Organisation par pays avec tabs */}
              <div className="mb-3 relative">
                {/* Indicateur de défilement à droite */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent z-10 flex items-center justify-center pointer-events-none">
                  <div className="animate-bounce-x">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </div>
                </div>
                
                <div className="flex overflow-x-auto pb-2 gap-2 border-b border-gray-200 scrollbar-hide relative">
                  {[
                    { id: 'ci', name: 'Côte d\'Ivoire', flag: '🇨🇮' },
                    { id: 'senegal', name: 'Sénégal', flag: '🇸🇳' },
                    { id: 'benin', name: 'Bénin', flag: '🇧🇯' },
                    { id: 'togo', name: 'Togo', flag: '🇹🇬' },
                    { id: 'mali', name: 'Mali', flag: '🇲🇱' },
                    { id: 'burkina', name: 'Burkina', flag: '🇧🇫' }
                  ].map((country, index) => {
                    const isActive = (
                      (country.id === 'ci' && modePaiement.includes('-ci')) ||
                      (country.id === 'senegal' && modePaiement.includes('senegal')) ||
                      (country.id === 'benin' && modePaiement.includes('benin')) ||
                      (country.id === 'togo' && modePaiement.includes('togo')) ||
                      (country.id === 'mali' && modePaiement.includes('mali')) ||
                      (country.id === 'burkina' && modePaiement.includes('burkina'))
                    );
                    
                    return (
                      <button
                        key={country.id}
                        onClick={() => {
                          // Sélectionner le premier opérateur de ce pays
                          const operators = {
                            'ci': 'orange-money-ci',
                            'senegal': 'orange-money-senegal',
                            'benin': 'mtn-benin',
                            'togo': 't-money-togo',
                            'mali': 'orange-money-mali',
                            'burkina': 'orange-money-burkina'
                          };
                          setModePaiement(operators[country.id]);
                        }}
                        className={`px-4 py-2 rounded-t-lg flex-shrink-0 flex items-center min-w-max ${
                          isActive 
                            ? 'bg-blue-50 text-blue-600 font-medium border-b-2 border-blue-500' 
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <span className="mr-2">{country.flag}</span>
                        {country.name}
                      </button>
                    );
                  })}
                </div>
                
                {/* Style pour cacher la scrollbar mais permettre le défilement */}
                <style jsx>{`
                  .scrollbar-hide {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;  /* Firefox */
                  }
                  .scrollbar-hide::-webkit-scrollbar {
                    display: none;  /* Chrome, Safari, Opera */
                  }
                  @keyframes bounce-x {
                    0%, 100% {
                      transform: translateX(-25%);
                      animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
                    }
                    50% {
                      transform: translateX(0);
                      animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
                    }
                  }
                  .animate-bounce-x {
                    animation: bounce-x 1s infinite;
                  }
                `}</style>
              </div>

              {/* Côte d'Ivoire */}
              {modePaiement.includes('-ci') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('orange-money-ci')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'orange-money-ci' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'orange-money-ci' ? 'border-orange-500' : 'border-gray-300'}`}>
                      {modePaiement === 'orange-money-ci' && <div className="w-3 h-3 bg-orange-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇨🇮</span>
                    <span className="font-medium">Orange Money CI</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('mtn-ci')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'mtn-ci' ? 'border-yellow-500 bg-yellow-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'mtn-ci' ? 'border-yellow-500' : 'border-gray-300'}`}>
                      {modePaiement === 'mtn-ci' && <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇨🇮</span>
                    <span className="font-medium">MTN CI</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('moov-ci')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'moov-ci' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'moov-ci' ? 'border-blue-500' : 'border-gray-300'}`}>
                      {modePaiement === 'moov-ci' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇨🇮</span>
                    <span className="font-medium">Moov CI</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('wave-ci')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'wave-ci' ? 'border-teal-500 bg-teal-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'wave-ci' ? 'border-teal-500' : 'border-gray-300'}`}>
                      {modePaiement === 'wave-ci' && <div className="w-3 h-3 bg-teal-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇨🇮</span>
                    <span className="font-medium">Wave CI</span>
                  </div>
                </div>
              )}
              
              {/* Sénégal */}
              {modePaiement.includes('senegal') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('orange-money-senegal')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'orange-money-senegal' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'orange-money-senegal' ? 'border-orange-500' : 'border-gray-300'}`}>
                      {modePaiement === 'orange-money-senegal' && <div className="w-3 h-3 bg-orange-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇸🇳</span>
                    <span className="font-medium">Orange Money</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('free-money-senegal')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'free-money-senegal' ? 'border-purple-500 bg-purple-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'free-money-senegal' ? 'border-purple-500' : 'border-gray-300'}`}>
                      {modePaiement === 'free-money-senegal' && <div className="w-3 h-3 bg-purple-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇸🇳</span>
                    <span className="font-medium">Free Money</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('expresso-senegal')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'expresso-senegal' ? 'border-red-500 bg-red-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'expresso-senegal' ? 'border-red-500' : 'border-gray-300'}`}>
                      {modePaiement === 'expresso-senegal' && <div className="w-3 h-3 bg-red-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇸🇳</span>
                    <span className="font-medium">E-Money</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('wave-senegal')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'wave-senegal' ? 'border-teal-500 bg-teal-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'wave-senegal' ? 'border-teal-500' : 'border-gray-300'}`}>
                      {modePaiement === 'wave-senegal' && <div className="w-3 h-3 bg-teal-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇸🇳</span>
                    <span className="font-medium">Wave</span>
                  </div>
                </div>
              )}
              
              {/* Bénin */}
              {modePaiement.includes('benin') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('mtn-benin')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'mtn-benin' ? 'border-yellow-500 bg-yellow-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'mtn-benin' ? 'border-yellow-500' : 'border-gray-300'}`}>
                      {modePaiement === 'mtn-benin' && <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇧🇯</span>
                    <span className="font-medium">MTN Bénin</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('moov-benin')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'moov-benin' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'moov-benin' ? 'border-blue-500' : 'border-gray-300'}`}>
                      {modePaiement === 'moov-benin' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇧🇯</span>
                    <span className="font-medium">Moov Bénin</span>
                  </div>
                </div>
              )}
              
              {/* Togo */}
              {modePaiement.includes('togo') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('t-money-togo')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 't-money-togo' ? 'border-green-500 bg-green-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 't-money-togo' ? 'border-green-500' : 'border-gray-300'}`}>
                      {modePaiement === 't-money-togo' && <div className="w-3 h-3 bg-green-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇹🇬</span>
                    <span className="font-medium">T-Money Togo</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('moov-togo')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'moov-togo' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'moov-togo' ? 'border-blue-500' : 'border-gray-300'}`}>
                      {modePaiement === 'moov-togo' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇹🇬</span>
                    <span className="font-medium">Moov Togo</span>
                  </div>
                </div>
              )}
              
              {/* Mali */}
              {modePaiement.includes('mali') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('orange-money-mali')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'orange-money-mali' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'orange-money-mali' ? 'border-orange-500' : 'border-gray-300'}`}>
                      {modePaiement === 'orange-money-mali' && <div className="w-3 h-3 bg-orange-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇲🇱</span>
                    <span className="font-medium">Orange Money Mali</span>
                  </div>
                </div>
              )}
              
              {/* Burkina */}
              {modePaiement.includes('burkina') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div 
                    onClick={() => setModePaiement('orange-money-burkina')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'orange-money-burkina' ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'orange-money-burkina' ? 'border-orange-500' : 'border-gray-300'}`}>
                      {modePaiement === 'orange-money-burkina' && <div className="w-3 h-3 bg-orange-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇧🇫</span>
                    <span className="font-medium">Orange Money Burkina</span>
                  </div>
                  
                  <div 
                    onClick={() => setModePaiement('moov-burkina-faso')}
                    className={`flex items-center p-3 border rounded-xl cursor-pointer transition ${modePaiement === 'moov-burkina-faso' ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 mr-2 flex items-center justify-center ${modePaiement === 'moov-burkina-faso' ? 'border-blue-500' : 'border-gray-300'}`}>
                      {modePaiement === 'moov-burkina-faso' && <div className="w-3 h-3 bg-blue-500 rounded-full"></div>}
                    </div>
                    <span className="mr-2 text-lg">🇧🇫</span>
                    <span className="font-medium">Moov Burkina</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}
            
            {/* Bouton de soumission */}
            <button 
              onClick={handleSubmit}
              className="w-full py-4 text-white bg-indigo-600 rounded-xl font-medium flex items-center justify-center gap-2 shadow-sm"
              disabled={isBusy}
            >
              {isBusy ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Traitement...
                </>
              ) : (
                userInfos?.retraitAuto?.active ? 'Mettre à jour' : 'Activer'
              )}
            </button>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-0.5">
              <AlertCircle className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm text-blue-700">
                En activant les paiements automatiques, vous recevrez tous vos revenus directement sur votre compte Mobile Money dès qu'un paiement est effectué sur la plateforme.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationRetrait;